<script setup lang="ts">
import { h, reactive, useAttrs, useSlots, watch, Fragment, normalizeClass, type VNode } from "vue";
import { Column, ColumnGroup, Row as ColumnGroupRow } from "./LISADataTableDeclarations";
import {
  LISADataTable,
  LISAButton,
  type LISADataTableAction,
  type LISADataTableColumn,
  type LISADataTableColumnGroup,
  type LISADataTablePageEvent,
  type LISADataTableRowReorderEvent,
  type LISADataTableSortMeta,
} from "@fi-solutions/lisa-ui";
import { useTranslation } from "@/language/translation";

defineOptions({ inheritAttrs: false });

type Row = Record<string, unknown>;
// Legacy table callbacks span unrelated row shapes and PrimeVue event signatures.
// The adapter validates values before passing them to the typed Lisa UI component.
type LegacyPayload = any;
type FilterConstraint = { value: unknown; matchMode?: string };
type FilterEntry = FilterConstraint | { operator: string; constraints: FilterConstraint[] };
type Filters = Record<string, FilterEntry>;

const props = withDefaults(
  defineProps<{
    value?: readonly Row[];
    dataKey?: string;
    loading?: boolean;
    selection?: LegacyPayload;
    selectionMode?: string;
    expandedRows?: Record<string, boolean> | readonly (string | number)[];
    sortField?: string;
    sortOrder?: number;
    multiSortMeta?: LegacyPayload[];
    filters?: Record<string, LegacyPayload>;
    globalFilterFields?: string[];
    paginator?: boolean;
    first?: number;
    rows?: number;
    rowsPerPageOptions?: number[];
    totalRecords?: number;
    lazy?: boolean;
    scrollable?: boolean;
    scrollHeight?: string;
    virtualScrollerOptions?: { itemSize: number };
    rowClass?: (row: LegacyPayload) => unknown;
    rowStyle?: (row: LegacyPayload) => LegacyPayload;
    rowAttrs?: (row: LegacyPayload, index: number) => Record<string, unknown>;
    cellAttrs?: (
      row: LegacyPayload,
      index: number,
      column: LISADataTableColumn,
    ) => Record<string, unknown>;
    expanderButtonAttrs?: (row: LegacyPayload, index: number) => Record<string, unknown>;
    stripedRows?: boolean;
    showGridlines?: boolean;
    rowHover?: boolean;
    size?: "small" | "normal" | "large";
    resizableColumns?: boolean;
    hideSelectionControls?: boolean;
    clickableRows?: boolean;
    navigationMode?: boolean;
    selectableRow?: (row: LegacyPayload) => boolean;
    columnSizing?: Record<string, "content" | "fill">;
    actions?: readonly LISADataTableAction<Row>[];
  }>(),
  {
    value: () => [],
    dataKey: undefined,
    loading: false,
    selection: null,
    selectionMode: undefined,
    expandedRows: () => ({}),
    sortField: undefined,
    sortOrder: 0,
    multiSortMeta: () => [],
    filters: () => ({}),
    globalFilterFields: () => [],
    paginator: false,
    first: 0,
    rows: 10,
    rowsPerPageOptions: () => [],
    totalRecords: undefined,
    lazy: false,
    scrollable: false,
    scrollHeight: undefined,
    virtualScrollerOptions: undefined,
    rowClass: undefined,
    rowStyle: undefined,
    rowAttrs: undefined,
    cellAttrs: undefined,
    expanderButtonAttrs: undefined,
    stripedRows: false,
    showGridlines: false,
    rowHover: false,
    size: "normal",
    resizableColumns: false,
    hideSelectionControls: false,
    clickableRows: false,
    navigationMode: false,
    selectableRow: undefined,
    columnSizing: () => ({}),
    actions: () => [],
  },
);

const emit = defineEmits<{
  "update:selection": [value: LegacyPayload];
  "update:sortField": [value: string | undefined];
  "update:sortOrder": [value: 1 | -1 | 0];
  "update:multiSortMeta": [value: LegacyPayload[]];
  "update:filters": [value: LegacyPayload];
  "update:first": [value: number];
  "update:rows": [value: number];
  "update:expandedRows": [value: Record<string, boolean>];
  rowClick: [event: LegacyPayload];
  rowSelect: [event: LegacyPayload];
  rowUnselect: [event: LegacyPayload];
  rowReorder: [event: LISADataTableRowReorderEvent<Row>];
  page: [event: LegacyPayload];
  columnResizeEnd: [event: { element: HTMLElement; delta: number }];
}>();

const attrs = useAttrs();
const slots = useSlots();
const { t } = useTranslation();
const filterDrafts = reactive<Record<string, FilterConstraint>>({});

watch(
  () => props.filters,
  (filters) => {
    for (const [field, entry] of Object.entries(filters)) {
      filterDrafts[field] = { ...(isOperatorFilter(entry) ? entry.constraints[0] : entry) };
    }
  },
  { immediate: true, deep: true },
);

function isOperatorFilter(
  entry: FilterEntry,
): entry is { operator: string; constraints: FilterConstraint[] } {
  return "constraints" in entry;
}

function flattenFragments(nodes: VNode[]): VNode[] {
  return nodes.flatMap((node) => {
    if (node.type === Fragment && Array.isArray(node.children))
      return flattenFragments(node.children as VNode[]);
    return [node];
  });
}

type ColumnNode = {
  field: string;
  props: Record<string, unknown>;
  slots: Record<string, (scope: Record<string, unknown>) => VNode[]>;
};

function getColumns(): ColumnNode[] {
  return flattenFragments(slots.default?.() ?? [])
    .filter((node) => node.type === Column)
    .map((node, index) => {
      const raw = (node.props ?? {}) as Record<string, unknown>;
      const children = node.children && !Array.isArray(node.children) ? node.children : {};
      return {
        field: String(raw.field ?? raw.name ?? `__column_${index}`),
        props: raw,
        slots: children as ColumnNode["slots"],
      };
    });
}

function columnGroups(): LISADataTableColumnGroup[] {
  const group = flattenFragments(slots.default?.() ?? []).find((node) => node.type === ColumnGroup);
  if (!group || !group.children || Array.isArray(group.children)) return [];
  const groupSlots = group.children as { default?: () => VNode[] };
  const row = flattenFragments(groupSlots.default?.() ?? []).find(
    (node) => node.type === ColumnGroupRow,
  );
  if (!row || !row.children || Array.isArray(row.children)) return [];
  const rowSlots = row.children as { default?: () => VNode[] };
  const cells = flattenFragments(rowSlots.default?.() ?? []).filter((node) => node.type === Column);
  return cells.map((cell) => ({
    header: String(cell.props?.header ?? ""),
    colspan: Number(cell.props?.colspan ?? 1),
  }));
}

function columnWidth(raw: Record<string, unknown>): string | undefined {
  const style = raw.headerStyle ?? raw.style;
  if (style && typeof style === "object" && "width" in style) return String(style.width);
  if (typeof style === "string") return /(?:^|;)\s*width\s*:\s*([^;]+)/.exec(style)?.[1]?.trim();
  return undefined;
}

function activeConstraint(constraint: FilterConstraint): boolean {
  return constraint.value !== null && constraint.value !== undefined && constraint.value !== "";
}

function enabledBooleanAttribute(value: unknown): boolean {
  return value !== undefined && value !== null && value !== false && value !== "false";
}

function matches(row: Row, field: string, constraint: FilterConstraint): boolean {
  if (!activeConstraint(constraint)) return true;
  if (!constraint.matchMode) return true;
  const value = field
    .split(".")
    .reduce<unknown>(
      (current, key) =>
        current && typeof current === "object"
          ? (current as Record<string, unknown>)[key]
          : undefined,
      row,
    );
  const query = constraint.value;
  const source = String(value ?? "").toLocaleLowerCase();
  const text = String(query ?? "").toLocaleLowerCase();
  switch (constraint.matchMode) {
    case "contains":
      return source.includes(text);
    case "notContains":
      return !source.includes(text);
    case "startsWith":
      return source.startsWith(text);
    case "endsWith":
      return source.endsWith(text);
    case "equals":
    case "dateIs":
      return source === text;
    case "notEquals":
    case "dateIsNot":
      return source !== text;
    case "dateAfter":
    case "gt":
      return value != null && new Date(String(value)).getTime() > new Date(String(query)).getTime();
    case "dateBefore":
    case "lt":
      return value != null && new Date(String(value)).getTime() < new Date(String(query)).getTime();
    case "in":
      return Array.isArray(query) && query.includes(value);
    default:
      return source.includes(text);
  }
}

function matchesLegacyFilters(row: Row): boolean {
  for (const [field, entry] of Object.entries(props.filters)) {
    if (field === "global") continue;
    if (isOperatorFilter(entry)) {
      const active = entry.constraints.filter(activeConstraint);
      if (
        active.length &&
        !(entry.operator === "and"
          ? active.every((item) => matches(row, field, item))
          : active.some((item) => matches(row, field, item)))
      )
        return false;
    } else if (!matches(row, field, entry)) return false;
  }
  const global = props.filters.global;
  if (
    global &&
    !isOperatorFilter(global) &&
    activeConstraint(global) &&
    props.globalFilterFields.length
  ) {
    return props.globalFilterFields.some((field) => matches(row, field, global));
  }
  return true;
}

function applyFilter(field: string) {
  const updated: Filters = { ...props.filters };
  const current = updated[field];
  if (current) {
    updated[field] = isOperatorFilter(current)
      ? { ...current, constraints: [{ ...filterDrafts[field] }, ...current.constraints.slice(1)] }
      : { ...filterDrafts[field] };
  }
  emit("update:filters", updated);
}

function clearFilter(field: string) {
  filterDrafts[field] = { ...filterDrafts[field], value: null };
  applyFilter(field);
}

function renderTable() {
  const declarations = getColumns();
  const declaredSelectionMode = declarations.find((column) => column.props.selectionMode)?.props
    .selectionMode;
  const effectiveSelectionMode = props.selectionMode ?? declaredSelectionMode;
  const reorderableRows = declarations.some((column) =>
    enabledBooleanAttribute(column.props.rowReorder ?? column.props["row-reorder"]),
  );
  const expandable =
    declarations.some((column) => enabledBooleanAttribute(column.props.expander)) ||
    Boolean(slots.expansion);
  const customExpander = false;
  const filterActive: Record<string, boolean> = {};
  const columns: LISADataTableColumn[] = declarations
    .filter(
      (column) =>
        !column.props.selectionMode &&
        !enabledBooleanAttribute(column.props.rowReorder ?? column.props["row-reorder"]),
    )
    .map((column) => ({
      field: column.field,
      header: String(column.props.header ?? ""),
      expander: enabledBooleanAttribute(column.props.expander),
      expandIcon: typeof column.props.expandIcon === "string" ? column.props.expandIcon : undefined,
      collapseIcon:
        typeof column.props.collapseIcon === "string" ? column.props.collapseIcon : undefined,
      sortable: enabledBooleanAttribute(column.props.sortable),
      filterable: Boolean(column.slots.filter),
      frozen: enabledBooleanAttribute(column.props.frozen),
      width: columnWidth(column.props),
      sizing: enabledBooleanAttribute(column.props.expander)
        ? "content"
        : props.columnSizing[column.field],
      headerClass: normalizeClass([column.props.headerClass, column.props.class]),
      bodyClass: typeof column.props.bodyClass === "string" ? column.props.bodyClass : undefined,
      headerStyle: column.props.headerStyle as string | Record<string, string> | undefined,
      bodyStyle: (column.props.bodyStyle ?? column.props.style) as
        | string
        | Record<string, string>
        | undefined,
    }));
  const expandedRows = Array.isArray(props.expandedRows)
    ? props.expandedRows
    : Object.keys(props.expandedRows ?? {}).filter(
        (key) => (props.expandedRows as Record<string, boolean>)[key],
      );
  const tableSlots: Record<string, (scope: Record<string, unknown>) => VNode[] | VNode> = {};
  if (slots.header) tableSlots.header = () => slots.header?.() ?? [];
  if (slots.empty) tableSlots.empty = () => slots.empty?.() ?? [];
  if (slots.expansion) tableSlots.expansion = (scope) => slots.expansion?.(scope) ?? [];
  if (slots.footer) tableSlots.footer = () => slots.footer?.() ?? [];
  for (const column of declarations) {
    if (column.props.selectionMode || column.props.rowReorder || column.props.expander) continue;
    if (column.slots.body) {
      tableSlots[`cell-${column.field}`] = (scope) =>
        column.slots.body({
          data: scope.data,
          field: column.field,
          index: scope.index,
          column: { props: column.props },
        });
    }
    if (column.slots.header)
      tableSlots[`header-${column.field}`] = () =>
        column.slots.header({ column: { props: column.props } });
    if (column.slots.filter) {
      const field = String(column.props.filterField ?? column.field);
      const entry = props.filters[field];
      filterActive[column.field] = Boolean(
        entry && activeConstraint(isOperatorFilter(entry) ? entry.constraints[0] : entry),
      );
      tableSlots[`filter-${column.field}`] = () => {
        const model = filterDrafts[field] ?? { value: null, matchMode: "contains" };
        return [
          ...column.slots.filter({ filterModel: model, filterCallback: () => applyFilter(field) }),
          h("div", { class: "flex gap-2" }, [
            h(
              LISAButton,
              { size: "sm", variant: "neutral", onClick: () => clearFilter(field) },
              () => t("primevue.clear"),
            ),
            h(
              LISAButton,
              { size: "sm", variant: "primary", onClick: () => applyFilter(field) },
              () => t("primevue.apply"),
            ),
          ]),
        ];
      };
    }
  }
  const rootAttrs = Object.fromEntries(
    Object.entries(attrs).filter(
      ([key]) =>
        key === "class" ||
        key === "style" ||
        key === "id" ||
        key === "title" ||
        key.startsWith("data-") ||
        key.startsWith("aria-"),
    ),
  );
  return h(
    LISADataTable,
    {
      ...rootAttrs,
      value: props.value,
      rowFilter: matchesLegacyFilters,
      columns,
      actions: props.actions,
      actionsLabel: t("global.actions"),
      columnGroups: columnGroups(),
      filterActive,
      clickableRows: props.clickableRows,
      navigationMode: props.navigationMode,
      expanderButtonAttrs: props.expanderButtonAttrs,
      hideSelectionControls: props.hideSelectionControls,
      selectableRow: props.selectableRow,
      resizableColumns: props.resizableColumns,
      customExpander,
      dataKey: props.dataKey,
      loading: props.loading,
      selection: props.selection,
      selectionMode:
        effectiveSelectionMode === "single" || effectiveSelectionMode === "multiple"
          ? effectiveSelectionMode
          : undefined,
      expandedRows,
      sortField: props.sortField,
      sortOrder: props.sortOrder === 1 || props.sortOrder === -1 ? props.sortOrder : 0,
      multiSortMeta: props.multiSortMeta.filter(
        (item): item is LISADataTableSortMeta =>
          typeof item.field === "string" && (item.order === 1 || item.order === -1),
      ),
      paginator: props.paginator,
      first: props.first,
      rows: props.rows,
      rowsPerPageOptions: props.rowsPerPageOptions,
      totalRecords: props.totalRecords,
      lazy: props.lazy,
      scrollHeight: props.scrollable
        ? props.scrollHeight === "flex"
          ? "100%"
          : props.scrollHeight
        : undefined,
      virtualRowHeight: props.virtualScrollerOptions?.itemSize,
      stripedRows: props.stripedRows,
      showGridlines: props.showGridlines,
      rowHover: props.rowHover,
      size: props.size === "small" ? "small" : undefined,
      rowClass: props.rowClass ? (row: Row) => normalizeClass(props.rowClass?.(row)) : undefined,
      rowStyle: props.rowStyle,
      rowAttrs: props.rowAttrs,
      cellAttrs: props.cellAttrs,
      reorderableRows,
      expandable,
      searchLabel: t("global.search"),
      loadingMessage: t("global.loading"),
      emptyMessage: t("global.noData"),
      previousPageLabel: t("primevue.aria.previousPageLabel"),
      nextPageLabel: t("primevue.aria.nextPageLabel"),
      rowsPerPageLabel: t("primevue.aria.rowsPerPageLabel"),
      selectAllLabel: t("primevue.aria.selectAll"),
      selectRowLabel: t("primevue.aria.selectRow"),
      expandRowLabel: t("primevue.aria.expandRow"),
      collapseRowLabel: t("primevue.aria.collapseRow"),
      filterLabel: t("primevue.aria.showFilterMenu"),
      clearFilterLabel: t("primevue.clear"),
      sortAscendingLabel: t("views.systemSettings.GeneralSystemSettings.plbSort.options.ASC"),
      sortDescendingLabel: t("views.systemSettings.GeneralSystemSettings.plbSort.options.DESC"),
      removeSortLabel: t("primevue.clear"),
      onRowClick: (event: { data: Row; index: number; originalEvent: Event }) =>
        emit("rowClick", event),
      onRowSelect: (event: { data: Row; index: number; originalEvent: Event }) =>
        emit("rowSelect", event),
      onRowUnselect: (event: { data: Row; index: number; originalEvent: Event }) =>
        emit("rowUnselect", event),
      onRowReorder: (event: LISADataTableRowReorderEvent<Row>) => emit("rowReorder", event),
      onPage: (event: LISADataTablePageEvent) => emit("page", event),
      onColumnResizeEnd: (event: { element: HTMLElement; delta: number }) =>
        emit("columnResizeEnd", event),
      "onUpdate:selection": (value: Row | Row[] | null) => emit("update:selection", value),
      "onUpdate:sortField": (value: string | undefined) => emit("update:sortField", value),
      "onUpdate:sortOrder": (value: 1 | -1 | 0) => emit("update:sortOrder", value),
      "onUpdate:multiSortMeta": (value: LISADataTableSortMeta[]) =>
        emit("update:multiSortMeta", value),
      "onUpdate:first": (value: number) => emit("update:first", value),
      "onUpdate:rows": (value: number) => emit("update:rows", value),
      "onUpdate:expandedRows": (value: (string | number)[]) =>
        emit("update:expandedRows", Object.fromEntries(value.map((key) => [String(key), true]))),
    },
    tableSlots,
  );
}
</script>

<template>
  <component :is="renderTable" />
</template>
